import { useEffect, useState } from "react";
import { useTranslator } from "@eo-locale/react";

import { useRouter } from "next/router";
import Cookies from "js-cookie";

import { useAppSelector, useAppDispatch } from "@/redux/store/hooks";

import {
  Box,
  ConditionalWrap,
  FiShare2,
  SocialShare,
  useBreakpointValue,
  useDisclosure,
  useToast
} from "@/storybook-components";
import ContentDrawer from "../common/drawer";
import Ideas from "./ideas";
import Idea from "./idea";
import {
  handleVote,
  handleUnVote,
  getVotingStatusWrapper
} from "./vote/manage-votes";
import {
  setTriggerPoint,
  setUnverifiedModalOpen,
  showUnverifiedDisabledModal
} from "@/redux/features/unverified-modal/slice";
import {
  resetComments,
  resetIdea,
  setIdeaTransitionDrawer
} from "@/redux/features/brainstormers/slice";
import { fetchIdea } from "@/redux/features/brainstormers/thunk";
import useUnverifiedModal from "@/hooks/useUnverfiedModal";
import FlexiLayout from "@/components/shared/layouts/ehq/flexi-layout";
import ActionButton from "@/components/shared/layouts/ehq/flexi-layout/action-button";
import About from "./about";
import SearchContent from "../actions/search";
import SortFilterContent from "../actions/sort-filter";
import PostDrawer from "../actions/create/post-drawer";
import BudgetAllocation from "@/components/budget-allocation";
import useUser from "@/hooks/useUser";
import useAuthGate from "@/hooks/useAuthGate";
import useBudget from "@/hooks/useBudget";
import { Idea as IdeaType } from "@/types";
import type { TriggerPointCookiePayload } from "@/types/trigger-point-cookie";
import { getToolLocaleKeyFromLayout } from "@/helpers/get-tool-locale-key-from-layout";
import { toSentenceCase } from "@/helpers/to-sentence-case";
import { setTriggerPointCookie } from "@/helpers/set-trigger-point-cookie";

interface TriggerPoint {
  projectId: string;
  toolId: string;
  postId: string;
  triggeredSource: string;
  isTriggeredPointActive: boolean;
  ideaData?: IdeaType;
  pendingProfileGate?: boolean;
}

export const defaultTriggerPoint: TriggerPoint = {
  projectId: "",
  toolId: "",
  postId: "",
  triggeredSource: "",
  isTriggeredPointActive: false
};

const Content = (): JSX.Element => {
  const { translate } = useTranslator();
  const router = useRouter();
  const dispatch = useAppDispatch();
  const [postId, setPostId] = useState<string | undefined>();
  const [params, setParams] = useState({
    sortBy: "created_at",
    sortOrder: "desc",
    search: "",
    categoryId: null
  });
  const [pageNumber, setPageNumber] = useState(1);

  const { isUserAdmin, isRegisteredUserLoggedIn } = useUser();
  const { brainstormer, ideas, idea, meta, ideaError, status } = useAppSelector(
    (state) => state.brainstormers
  );
  const { features } = useAppSelector((state) => state.features);
  const isNewCookieConsentEnabled = features?.newCookieConsentManager?.enabled;

  const {
    id: brainstormerId,
    state: brainstormerState,
    name,
    votingLimit,
    layout,
    votingStartAt,
    votingEndAt,
    allowUnverifiedParticipation,
    toolConfigurationAttributes
  } = brainstormer;

  const {
    isGuestUser,
    isFullyRegistered,
    isGuestParticipationEnabled,
    requireProfileCompletion,
    openGuestLoginModal,
    suggestProfileCompletion
  } = useAuthGate();

  const guestParticipationAllowed =
    isGuestParticipationEnabled && allowUnverifiedParticipation;
  const unverifiedParticipationAllowed =
    !isGuestParticipationEnabled && allowUnverifiedParticipation;
  const toolAllowsGuestOrUnverifiedParticipationFlow =
    guestParticipationAllowed || unverifiedParticipationAllowed;

  const {
    canContribute,
    visibilityMode,
    state: projectState,
    roleInCurrentContext
  } = useAppSelector((state) => state.project.project);
  const { baseUrl } = useAppSelector((state) => state.adapter.adapter);
  const contributionName =
    toolConfigurationAttributes?.preferences?.contributionName || "";

  const { disableSocialMediaSharing } = useAppSelector(
    (state) => state.settings.settings
  );

  const projectId = useAppSelector((state) => state?.project?.project?.id);

  const { triggerPoint } = useAppSelector((state) => state.unverifiedModal);
  const [interactionTriggerPoint, setInteractionTriggerPoint] =
    useState(defaultTriggerPoint);
  const { onOpen, open, onClose } = useDisclosure();
  const contentDrawerZIndex = isNewCookieConsentEnabled ? 1200 : 1000;

  const queryId = router.query?.id;
  const cookieValues = Cookies.get("trigger_point");
  let parsedTriggerPointCookie: TriggerPointCookiePayload | undefined;
  if (cookieValues) {
    try {
      parsedTriggerPointCookie = JSON.parse(
        cookieValues
      ) as TriggerPointCookiePayload;
    } catch {
      parsedTriggerPointCookie = undefined;
    }
  }
  const { addTotalFund, removeTotalFund } = useBudget();

  const toast = useToast({
    isClosable: true,
    status: "error"
  });

  const splitActions = useBreakpointValue({
    base:
      disableSocialMediaSharing ||
      projectState === "archived" ||
      brainstormerState === "archived",
    lg: true
  });

  useEffect(() => {
    if (queryId && !ideaError) {
      onOpen();
    }
  }, [onOpen, queryId, router, ideaError]);

  useEffect(() => {
    if (ideaError) {
      if (router.query.idea) {
        delete router.query.idea;
        router.replace(router, "", { shallow: true });
      }
      onClose();
    }
    if (status === "ideaUpdated" || status === "closingIdea") onClose();
  }, [ideaError, status]);

  useEffect(() => {
    setInteractionTriggerPoint(triggerPoint);
  }, [triggerPoint]);

  /**
   * Resume budget / vote / comment / create-idea UX after authentication,
   * driven by the `trigger_point` cookie from guest login or legacy unverified flow.
   */
  useEffect(() => {
    if (!isRegisteredUserLoggedIn) return;

    const raw = Cookies.get("trigger_point");
    if (!raw) return;

    let cookie: TriggerPointCookiePayload;
    try {
      cookie = JSON.parse(raw) as TriggerPointCookiePayload;
    } catch (error) {
      console.error("Error parsing trigger_point cookie:", error);
      Cookies.remove("trigger_point");
      dispatch(setTriggerPoint(defaultTriggerPoint));
      return;
    }

    const source = cookie.triggeredSource;
    if (
      source !== "budget" &&
      source !== "vote" &&
      source !== "comment" &&
      source !== "new_idea"
    ) {
      return;
    }

    const clearResumeArtifacts = (): void => {
      Cookies.remove("trigger_point");
      dispatch(setTriggerPoint(defaultTriggerPoint));
    };

    const abandonResumeWithToast = (): void => {
      toast.closeAll();
      toast({
        title: translate("errors.somethingWentWrong"),
        status: "error",
        isClosable: true
      });
      clearResumeArtifacts();
    };

    const isPendingGate = Boolean(cookie.pendingProfileGate);
    const skipIncompleteGuestProfileGate =
      isGuestParticipationEnabled && isPendingGate && !isFullyRegistered;
    const skipWhenParticipationClosed =
      !isPendingGate && !toolAllowsGuestOrUnverifiedParticipationFlow;

    if (skipIncompleteGuestProfileGate || skipWhenParticipationClosed) {
      return;
    }

    const matchesCurrentTool =
      cookie.toolId == null ||
      String(cookie.toolId).trim() === String(brainstormerId ?? "").trim();
    const matchesCurrentProject =
      cookie.projectId == null ||
      String(cookie.projectId).trim() === String(projectId ?? "").trim();

    if (source === "new_idea") {
      if (!matchesCurrentTool || !matchesCurrentProject) {
        clearResumeArtifacts();
        return;
      }

      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            action: "new_idea"
          }
        },
        undefined,
        { shallow: true }
      );

      clearResumeArtifacts();
      return;
    }

    if (source === "comment") {
      if (!matchesCurrentTool || !matchesCurrentProject) {
        clearResumeArtifacts();
        return;
      }

      const ideaIdResumeRaw = cookie.id ?? cookie.postId;
      const ideaIdResume =
        ideaIdResumeRaw != null && String(ideaIdResumeRaw).trim().length > 0
          ? String(ideaIdResumeRaw).trim()
          : "";

      if (!ideaIdResume) {
        clearResumeArtifacts();
        return;
      }

      dispatch(resetIdea());
      dispatch(resetComments());
      dispatch(setIdeaTransitionDrawer("contribution"));

      router.replace(
        {
          pathname: router.pathname,
          query: {
            ...router.query,
            idea: ideaIdResume,
            comment: "true"
          }
        },
        undefined,
        { shallow: true }
      );

      onOpen();

      dispatch(
        fetchIdea({
          projectId: String(projectId ?? ""),
          brainstormerId: String(brainstormerId ?? ""),
          ideaId: ideaIdResume
        })
      );

      clearResumeArtifacts();
      return;
    }

    const postIdFromCookieRaw = cookie.postId;
    const postIdFromCookie =
      postIdFromCookieRaw != null && String(postIdFromCookieRaw).length > 0
        ? String(postIdFromCookieRaw)
        : "";

    if (!postIdFromCookie) {
      return;
    }

    const resolveCurrentIdea = (): IdeaType | undefined => {
      const fromList = ideas.find((i) => String(i?.id) === postIdFromCookie);
      if (fromList) {
        return fromList;
      }
      if (
        cookie.ideaData &&
        String(cookie.postId ?? "") === postIdFromCookie &&
        (source === "budget" || source === "vote")
      ) {
        return cookie.ideaData as IdeaType;
      }
      return idea as IdeaType | undefined;
    };

    const resolvedIdea = resolveCurrentIdea();

    if (source === "budget") {
      if (!resolvedIdea) {
        abandonResumeWithToast();
        return;
      }

      if (resolvedIdea?.currentUserAllocation) {
        removeTotalFund(postIdFromCookie);
      } else {
        addTotalFund(
          postIdFromCookie,
          resolvedIdea?.baItemConfig?.unitCap || 0
        );
      }

      clearResumeArtifacts();
      return;
    }

    if (!resolvedIdea) {
      abandonResumeWithToast();
      return;
    }

    const projFromCookie =
      cookie.projectId != null ? String(cookie.projectId).trim() : "";
    const voteProject =
      projFromCookie.length > 0 ? projFromCookie : String(projectId ?? "");

    const toolFromCookie =
      cookie.toolId != null ? String(cookie.toolId).trim() : "";
    const voteTool =
      toolFromCookie.length > 0 ? toolFromCookie : String(brainstormerId ?? "");

    if (resolvedIdea?.currentUserVoted) {
      handleUnVote(
        postIdFromCookie,
        resolvedIdea,
        dispatch,
        voteProject,
        voteTool,
        translate,
        toast
      );
    } else {
      handleVote(
        postIdFromCookie,
        resolvedIdea,
        dispatch,
        voteProject,
        voteTool,
        translate,
        toast,
        votingLimit
      );
    }

    clearResumeArtifacts();
  }, [
    addTotalFund,
    brainstormerId,
    toolAllowsGuestOrUnverifiedParticipationFlow,
    dispatch,
    idea,
    ideas,
    isFullyRegistered,
    isGuestParticipationEnabled,
    isRegisteredUserLoggedIn,
    onOpen,
    projectId,
    removeTotalFund,
    router,
    toast,
    translate,
    votingLimit
  ]);

  const findCurrentInteractedIdea = (id: string) => {
    const filteredIdeaInList = ideas.filter(
      (ideaItem) => String(ideaItem?.id) === String(id)
    );
    if (filteredIdeaInList.length) {
      return filteredIdeaInList[0];
    }

    // For budgeting / voting resume, reuse idea snapshot from cookie when IDs match.
    const cookiePost = parsedTriggerPointCookie?.postId;
    const cookieIdea =
      parsedTriggerPointCookie?.id != null
        ? String(parsedTriggerPointCookie.id)
        : undefined;
    if (
      parsedTriggerPointCookie?.ideaData &&
      (String(cookiePost) === String(id) ||
        (cookieIdea !== undefined && cookieIdea === String(id))) &&
      (parsedTriggerPointCookie?.triggeredSource === "budget" ||
        parsedTriggerPointCookie?.triggeredSource === "vote" ||
        parsedTriggerPointCookie?.triggeredSource === "comment")
    ) {
      return parsedTriggerPointCookie.ideaData;
    }

    return idea;
  };

  const processVotes = (
    project = projectId,
    tool = brainstormerId,
    post = postId
  ) => {
    if (isGuestUser && !toolAllowsGuestOrUnverifiedParticipationFlow) {
      return;
    }

    const filteredArray = findCurrentInteractedIdea(post);
    if (filteredArray?.currentUserVoted) {
      handleUnVote(
        post,
        filteredArray,
        dispatch,
        project,
        tool,
        translate,
        toast
      );
    } else {
      handleVote(
        post,
        filteredArray,
        dispatch,
        project,
        tool,
        translate,
        toast,
        votingLimit
      );
    }
  };

  const processBudget = (
    post: string = postId,
    onAfterBudgetSuccessToast?: () => void
  ) => {
    if (isGuestUser && !toolAllowsGuestOrUnverifiedParticipationFlow) {
      return;
    }

    const currentInteractedIdea = findCurrentInteractedIdea(post);

    if (!currentInteractedIdea) {
      toast.closeAll();
      toast({
        title: translate("errors.somethingWentWrong"),
        status: "error",
        isClosable: true
      });
      return;
    }

    if (currentInteractedIdea?.currentUserAllocation) {
      removeTotalFund(post, onAfterBudgetSuccessToast);
    } else {
      addTotalFund(
        post,
        currentInteractedIdea?.baItemConfig?.unitCap || 0,
        onAfterBudgetSuccessToast
      );
    }
  };

  useUnverifiedModal(allowUnverifiedParticipation, (isProceedAllowed) => {
    if (isProceedAllowed) {
      const guestMustCompleteProfile =
        isGuestUser && !toolAllowsGuestOrUnverifiedParticipationFlow;
      const pendingProfileGate = Boolean(
        interactionTriggerPoint.pendingProfileGate ||
        parsedTriggerPointCookie?.pendingProfileGate
      );

      if (
        (interactionTriggerPoint.triggeredSource === "vote" &&
          interactionTriggerPoint.isTriggeredPointActive === true) ||
        parsedTriggerPointCookie?.triggeredSource === "vote"
      ) {
        if (!pendingProfileGate && !guestMustCompleteProfile) {
          parsedTriggerPointCookie
            ? processVotes(
                parsedTriggerPointCookie.projectId,
                parsedTriggerPointCookie.toolId,
                parsedTriggerPointCookie.postId
              )
            : processVotes();
        }
      }
      if (
        (interactionTriggerPoint.triggeredSource === "budget" &&
          interactionTriggerPoint.isTriggeredPointActive === true) ||
        parsedTriggerPointCookie?.triggeredSource === "budget"
      ) {
        if (!pendingProfileGate && !guestMustCompleteProfile) {
          parsedTriggerPointCookie
            ? processBudget(parsedTriggerPointCookie.postId)
            : processBudget();
        }
      }

      dispatch(
        setTriggerPoint({ ...triggerPoint, isTriggeredPointActive: false })
      );
      Cookies.remove("trigger_point");
    }
  });

  const handleWarningToast = (id: string, toastTitle: string): void => {
    if (!toast.isActive(id)) {
      toast({
        title: translate(toastTitle),
        id: id,
        status: "warning"
      });
    }
  };

  const checkVotingEligibility = (): boolean => {
    const voteConditions = [
      {
        condition: () =>
          projectState === "archived" || brainstormerState === "archived",
        toastTitle: "brainstormers.archivedError"
      },
      {
        condition: () => visibilityMode === "Panelled" && !canContribute,
        toastTitle: "brainstormers.notPartOfPannelledGroup"
      },
      {
        condition: () =>
          getVotingStatusWrapper(votingStartAt, votingEndAt) === "not started",
        toastTitle: "brainstormers.votingPeriodNotStarted"
      },
      {
        condition: () =>
          getVotingStatusWrapper(votingStartAt, votingEndAt) === "ended",
        toastTitle: "brainstormers.votingPeriodEnded"
      }
    ];

    for (const { condition, toastTitle } of voteConditions) {
      if (condition()) {
        handleWarningToast("restrict-vote", toastTitle);
        return false;
      }
    }
    return true;
  };

  const manageVotes = (id) => {
    if (!checkVotingEligibility()) {
      return;
    }

    setPostId(id);

    const currentIdeaData = ideas.find((i) => i?.id === id) || idea;

    const fireVoteTrigger = (
      isTriggeredPointActive: boolean,
      pendingProfileGate?: boolean
    ) => {
      const tp: TriggerPoint = {
        projectId,
        toolId: brainstormerId,
        postId: id,
        triggeredSource: "vote",
        isTriggeredPointActive,
        ideaData: currentIdeaData as IdeaType | undefined,
        ...(pendingProfileGate ? { pendingProfileGate: true } : {})
      };
      Cookies.set("trigger_point", JSON.stringify(tp), {
        expires: 1,
        secure: true,
        sameSite: "Strict"
      });
      dispatch(setTriggerPoint(tp));
    };

    if (isGuestUser) {
      if (!allowUnverifiedParticipation) {
        if (!isGuestParticipationEnabled) {
          fireVoteTrigger(false);
          dispatch(setUnverifiedModalOpen(false));
          dispatch(showUnverifiedDisabledModal(true));
          return;
        }
        fireVoteTrigger(false, true);
        requireProfileCompletion({
          reason: "profileGate.requiredForContribution",
          source: "vote"
        });
        return;
      }

      processVotes(projectId, brainstormerId, id);
      return;
    }

    if (isFullyRegistered) {
      processVotes(projectId, brainstormerId, id);
      return;
    }

    if (
      toolAllowsGuestOrUnverifiedParticipationFlow &&
      !isRegisteredUserLoggedIn
    ) {
      if (isGuestParticipationEnabled) {
        const inactiveVoteTrigger = {
          projectId,
          toolId: brainstormerId,
          postId: id,
          triggeredSource: "vote",
          isTriggeredPointActive: false,
          ideaData: currentIdeaData as IdeaType | undefined
        };
        setTriggerPointCookie(inactiveVoteTrigger);
        dispatch(setTriggerPoint(inactiveVoteTrigger));
        openGuestLoginModal();
      } else {
        fireVoteTrigger(true);
      }
      return;
    }

    fireVoteTrigger(true);
  };

  const checkBudgetingEligibility = (): boolean => {
    const budgetingConditions = [
      {
        condition: () =>
          projectState === "archived" || brainstormerState === "archived",
        toastTitle: "budget.archivedError"
      },
      {
        condition: () => visibilityMode === "Panelled" && !canContribute,
        toastTitle: "budget.notPartOfPannelledGroup"
      }
    ];

    for (const { condition, toastTitle } of budgetingConditions) {
      if (condition()) {
        handleWarningToast("restrict-budget", toastTitle);
        return false;
      }
    }
    return true;
  };

  const manageBudgeting = (id: string) => {
    if (!checkBudgetingEligibility()) return;

    setPostId(id);

    const currentIdeaData = ideas.find((idea) => idea?.id === id) || idea;

    const fireBudgetTrigger = (
      isTriggeredPointActive: boolean,
      pendingProfileGate?: boolean
    ) => {
      const budgetTp: TriggerPoint = {
        projectId,
        toolId: brainstormerId,
        postId: id,
        triggeredSource: "budget",
        isTriggeredPointActive,
        ideaData: currentIdeaData as IdeaType | undefined,
        ...(pendingProfileGate ? { pendingProfileGate: true } : {})
      };
      Cookies.set("trigger_point", JSON.stringify(budgetTp), {
        expires: 1,
        secure: true,
        sameSite: "Strict"
      });
      dispatch(setTriggerPoint(budgetTp));
    };

    if (isGuestUser) {
      if (!allowUnverifiedParticipation) {
        if (!isGuestParticipationEnabled) {
          fireBudgetTrigger(false);
          dispatch(setUnverifiedModalOpen(false));
          dispatch(showUnverifiedDisabledModal(true));
          return;
        }
        fireBudgetTrigger(false, true);
        requireProfileCompletion({
          reason: "profileGate.requiredForContribution",
          source: "budget"
        });
        return;
      }

      processBudget(id, () => {
        suggestProfileCompletion({
          reason: "profileGate.optionalSuggestion",
          source: "budget"
        });
      });
      return;
    }

    if (isFullyRegistered) {
      processBudget(id);
      return;
    }

    if (
      toolAllowsGuestOrUnverifiedParticipationFlow &&
      !isRegisteredUserLoggedIn
    ) {
      if (isGuestParticipationEnabled) {
        const inactiveBudgetTrigger = {
          projectId,
          toolId: brainstormerId,
          postId: id,
          triggeredSource: "budget",
          isTriggeredPointActive: false,
          ideaData: currentIdeaData as IdeaType | undefined
        };
        setTriggerPointCookie(inactiveBudgetTrigger);
        dispatch(setTriggerPoint(inactiveBudgetTrigger));
        openGuestLoginModal();
      } else {
        const pausedBudgetTrigger: TriggerPoint = {
          projectId,
          toolId: brainstormerId,
          postId: id,
          triggeredSource: "budget",
          isTriggeredPointActive: false,
          ideaData: currentIdeaData as IdeaType | undefined
        };
        setTriggerPointCookie(pausedBudgetTrigger as TriggerPointCookiePayload);
        dispatch(setTriggerPoint(pausedBudgetTrigger));
        dispatch(setUnverifiedModalOpen(false));
        dispatch(showUnverifiedDisabledModal(true));
      }
      return;
    }

    fireBudgetTrigger(true);
  };

  const onCloseHandler = () => {
    dispatch(setIdeaTransitionDrawer("none"));
    const routerQuery = router.query;
    if (routerQuery) {
      if (routerQuery.idea) delete routerQuery.idea;
      if (routerQuery.comment) delete routerQuery.comment;
      router.replace(router, "", { shallow: true });
    }
    onClose();
  };

  /**
   * Search Ideas Params
   */
  const searchParams = {
    projectId,
    brainstormerId,
    page: 1,
    sortOrder: params.sortOrder,
    sortBy: params.sortBy,
    categoryId: params.categoryId
  };

  /**
   * Sort Ideas Params
   */
  const sortParams = {
    projectId,
    brainstormerId,
    page: 1,
    search: params.search,
    categoryId: params.categoryId,
    sortOrder: params.sortOrder,
    sortBy: params.sortBy
  };

  const stateHandler = (sortBy, sortOrder, search, categoryId) => {
    setParams((prev) => ({
      sortBy,
      sortOrder,
      search,
      categoryId: categoryId !== undefined ? categoryId : prev.categoryId
    }));
  };

  return (
    <Box id="ideas-grid" data-testid="ideas-content">
      <FlexiLayout
        aboutContentPosition={layout === "BudgetAllocator" ? "top" : "left"}
        aboutContent={<About />}
        mainContent={
          <ConditionalWrap
            as={BudgetAllocation}
            condition={layout === "BudgetAllocator" && isRegisteredUserLoggedIn}
          >
            <Ideas
              count={meta.count}
              onOpen={onOpen}
              manageVotes={manageVotes}
              ideas={ideas}
              params={params}
              pageNumber={pageNumber}
              setPageNumber={setPageNumber}
              manageBudgeting={manageBudgeting}
            />
          </ConditionalWrap>
        }
        splitActions={splitActions}
        actions={[
          {
            position: "left",
            component: !disableSocialMediaSharing && (
              <SocialShare
                title={name}
                placement="auto-end"
                shareUrl={`${baseUrl}/${projectId}/brainstormers/${brainstormerId}`}
                trigger={
                  <ActionButton
                    type="icon"
                    icon={<FiShare2 />}
                    label={translate("brainstormers.shareThisPage")}
                    dataTestId="ideas-share-trigger"
                  />
                }
              />
            )
          },
          {
            position: "right",
            component: (
              <SearchContent
                open
                highlightClass="ideas-search-node"
                locale="brainstormers"
                action={stateHandler}
                resetPage={setPageNumber}
                {...searchParams}
              />
            )
          },
          {
            position: "right",
            component: (
              <SortFilterContent
                locale="brainstormers"
                action={stateHandler}
                resetPage={setPageNumber}
                {...sortParams}
              />
            )
          },
          (layout !== "BudgetAllocator" ||
            (isUserAdmin && roleInCurrentContext !== "participant")) && {
            position: "right",
            component: <PostDrawer params={params} />
          }
        ]}
      />
      <ContentDrawer
        zIndex={contentDrawerZIndex}
        open={open}
        onClose={onCloseHandler}
        onCloseComplete={() => {
          if (status === "editTriggered")
            dispatch(setIdeaTransitionDrawer("update"));
        }}
        header={toSentenceCase(
          contributionName.trim() ||
            translate(
              `tools.${getToolLocaleKeyFromLayout(layout)}.contributionName`
            )
        )}
        content={
          <Idea
            manageVotes={manageVotes}
            manageBudgeting={manageBudgeting}
            onContributionDrawerClose={onCloseHandler}
          />
        }
        trapFocus
        footer={null}
      />
    </Box>
  );
};

export default Content;
